import axios from 'axios';
import { setupAuthInterceptor } from '../common';
import { NAMESPACES } from '../configuration';
import { FEATURE_ROLLOUT_REQUEST_TIMEOUT_MS } from '../../configuration/directMenuRollout';

const api = setupAuthInterceptor(axios.create({
  baseURL: NAMESPACES.management_api.feature_rollout,
  timeout: FEATURE_ROLLOUT_REQUEST_TIMEOUT_MS,
}));

export interface DirectMenuState {
  enabled: boolean;
  revision: number;
}

export type ImplementedRolloutFeature = 'direct_menu' | 'staff_tips' | 'delegated_settings';

function stateFromResponse(value: unknown): DirectMenuState {
  if (typeof value !== 'object' || value === null) throw new Error('Invalid rollout response');
  const state = value as Record<string, unknown>;
  if (typeof state.enabled !== 'boolean' || typeof state.revision !== 'number'
    || !Number.isSafeInteger(state.revision) || state.revision < 0) {
    throw new Error('Invalid rollout response');
  }
  return { enabled: state.enabled, revision: state.revision };
}

/** Uncached read: stale browser state must not authorize overwriting another editor. */
export async function getDirectMenuState(localId: number, signal?: AbortSignal): Promise<DirectMenuState> {
  const response = await api.get(`/DirectMenu/${localId}`, { signal });
  return stateFromResponse(response.data);
}

/** Explicit CAS assignment; no automatic retry of an ambiguous write or conflict. */
export async function setDirectMenuState(
  localId: number, enabled: boolean, expectedRevision: number, signal?: AbortSignal,
): Promise<DirectMenuState> {
  const response = await api.put(`/DirectMenu/${localId}`, {
    enabled, expected_revision: expectedRevision,
  }, { signal });
  return stateFromResponse(response.data);
}

/** Read an implemented flag; unknown/unimplemented flags cannot be administered. */
export async function getFeatureState(
  localId: number, feature: ImplementedRolloutFeature, signal?: AbortSignal,
): Promise<DirectMenuState> {
  return stateFromResponse((await api.get(`/Feature/${feature}/${localId}`, { signal })).data);
}

/** Revisioned assignment preserves other independently configured flags. */
export async function setFeatureState(
  localId: number, feature: ImplementedRolloutFeature, enabled: boolean,
  expectedRevision: number, signal?: AbortSignal,
): Promise<DirectMenuState> {
  return stateFromResponse((await api.put(`/Feature/${feature}/${localId}`, {
    enabled, expected_revision: expectedRevision,
  }, { signal })).data);
}

export type StaffTips =
  | { state: 'disabled' | 'no_session' }
  | { state: 'active'; session_id: number; currency: string; collected_tips: string };

/** Validate the narrow no-cache projection; invalid money never becomes zero. */
export async function getStaffTips(localId: number, signal?: AbortSignal): Promise<StaffTips> {
  const response = await api.get(`/StaffTips/${localId}`, { signal });
  const value: unknown = response.data;
  if (typeof value !== 'object' || value === null) throw new Error('Invalid tip summary');
  const data = value as Record<string, unknown>;
  if (data.state === 'disabled' || data.state === 'no_session') return { state: data.state };
  if (data.state !== 'active' || typeof data.session_id !== 'number'
    || !Number.isSafeInteger(data.session_id) || data.session_id <= 0
    || typeof data.currency !== 'string' || !/^[A-Z]{3}$/.test(data.currency)
    || typeof data.collected_tips !== 'string' || !/^(0|[1-9]\d*)\.\d{2}$/.test(data.collected_tips)) {
    throw new Error('Invalid tip summary');
  }
  return {
    state: 'active', session_id: data.session_id,
    currency: data.currency, collected_tips: data.collected_tips,
  };
}
